/**
 * Client Ledger Service — BE-11 (`ClientLedgerController`, /api/reports/client-ledger). Real backend.
 * Returns `{ success, data }` with camelCase data, the shape the report pages use.
 * The portal calls give the signed-in customer its own statement (no float) and send the portal token.
 */

import ApiService from '../api.service';
import { keysSnakeToCamel, unwrapData } from '../../utils/apiConvert';

const BASE = '/api/reports/client-ledger';
const wrap = (res) => ({ success: res?.data?.success !== false, data: keysSnakeToCamel(res?.data?.data ?? null) });

const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));
const portalAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('portalToken')}` });
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// A failed blob request carries JSON in a Blob; turn it back into the server's message.
export const blobErrorMessage = async (err, fallback = 'Export failed') => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text());
      return body?.error || body?.message || fallback;
    } catch (e) {
      return fallback;
    }
  }
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
};

const saveBlob = (blob, filename, type) => {
  const href = URL.createObjectURL(new Blob([blob], { type }));
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60000);
};

export const clientLedgerService = {
  getClientList: async (params = {}) => wrap(await ApiService.get(BASE, { params })),

  // params: { from, to } as YYYY-MM-DD
  getClientStatement: async (orgId, params = {}) => wrap(await ApiService.get(`${BASE}/${orgId}`, { params })),

  getClientAging: async (orgId) => wrap(await ApiService.get(`${BASE}/${orgId}/aging`)),

  getGovtFeeFloat: async (orgId) => wrap(await ApiService.get(`${BASE}/${orgId}/govt-fee-float`)),

  // params: { format: 'pdf' | 'csv', from, to } → Blob
  exportStatement: async (orgId, params) => (await ApiService.get(`${BASE}/${orgId}/export`, { params, responseType: 'blob' })).data,

  // ── customer portal: the signed-in customer's own statement ──
  getPortalStatement: async (params) =>
    unwrapData(await ApiService.get('/api/portal/statement', { params: clean(params), headers: portalAuth() })),

  // format: 'pdf' | 'csv'; saves the file as statement-{today}.{format}
  exportPortalStatement: async (format, params) => {
    let res;
    try {
      res = await ApiService.get('/api/portal/statement/export', { params: clean({ format, ...params }), responseType: 'blob', headers: portalAuth() });
    } catch (err) {
      throw new Error(await blobErrorMessage(err));
    }
    saveBlob(res.data, `statement-${today()}.${format}`, format === 'pdf' ? 'application/pdf' : 'text/csv');
  },
};

export default clientLedgerService;
